// @givmo/org-console — MEMBERS: who can act for this organization, and who may change that.
//
// ⚠ THE ACTIONS COLUMN FAILS OPEN. Both the catalog read and the write are server-gated, so a
// permissive client gate changes nothing a Member can actually do — while a restrictive one locks a
// real Global Admin out of their own team over one flaky request. `isGlobalAdmin` is false only for a
// RESOLVED non-admin.
//
// ⚠ THERE IS NO SECOND ROSTER READ TO FALL BACK ON, and that is deliberate rather than a gap. The
// console this pattern comes from falls through to an older, ungated list when its console routes are
// dark — it could, because that older list already existed. `/org` has none: `GET /org/{ref}/me` is
// flag-gated ON PURPOSE, because it is a NEW route with no accurate information to preserve, so it
// goes dark with the surface it belongs to. Inventing a lesser list here and serving it as if it were
// the answer would be worse than saying plainly that the console is not switched on.
//
// ⚠ NO SCREEN CLAIMS A TOTAL. The paginated envelope's `meta` is discarded by the unwrap before this
// component can see it, so the footer counts rows LOADED and the pager asks only whether the last
// page came back short.
//
// DATA CONTRACT: the `<prefix>-members-*` test-id family. Treat a rename like an API change.

import { useCallback, useMemo, useState } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import {
  Avatar,
  Badge,
  Button,
  DataTable,
  EmptyState,
  Icon,
  testIdProp,
  type DataTableColumn,
} from '@givmo/design-system';
import { Users } from 'lucide-react';
import { isServedModule, testIdPrefix, type OrgConsoleConfig } from './org-console-config';
import { isOrgConsoleUnavailable } from './org-console-transport';
import { moduleAccessSummary, orgMemberEmail, orgMemberName } from './org-decode';
import { orgConsoleKeys } from './org-access-queries';
import { ROLE_MEMBER, ROLE_OWNER, moduleTierLabel, orgLevelWord } from './org-tiers';
import { useOrgAccessGate, useOrgMembers } from './use-org-console';
import { OrgAccessEditorDialog } from './org-access-editor-dialog';
import { OrgConsoleUnavailableNotice } from './org-console-notices';
import { ExternalNote } from './org-external';
import { TEAM_COPY } from './org-team-copy';
import type { OrgConsoleMemberRow } from './wire-types';

export interface OrgPanelProps {
  config: OrgConsoleConfig;
  /**
   * Show only the people who hold ONE module.
   *
   * ⚠ A CLIENT-SIDE NARROWING OF THE SAME READ, never a second one. Every roster row already carries
   * its owner's per-module standing, so "who can open this app" is a filter rather than a request —
   * which is why the management grid's per-module door needs no operation of its own.
   *
   * The pager keeps its own meaning: it loads more of the ROSTER, and the filter re-applies. So the
   * footer counts the people shown, under a sentence that names the narrowing.
   */
  moduleFilter?: string | null;
  /** Drop the filter. Omitted ⇒ no control is drawn, because nothing here can clear it. */
  onClearModuleFilter?: () => void;
  /**
   * The whole team, with one more column: each person's level in THIS app (OM-96). A Global Admin's
   * app Team page shows Team access's roster in place; this column keeps what the app's own page
   * always said — who is an Owner of it — without narrowing the list. Ignored with `moduleFilter`.
   */
  appColumn?: string | null;
  /**
   * The host's sign-out and door hop, for when the signed-in person removes THEMSELVES from the team
   * in the access editor (EP-MNT9 (c)). Absent ⇒ their row drops and they stay, as before.
   */
  onLeft?: () => void;
  /**
   * The host's page already carries this title, so render it for assistive technology alone.
   *
   * ⚠ HIDDEN, NEVER REMOVED. A page whose bar, heading and section title all name the same thing
   * says it three times before the first person — at phone, in one straight vertical run, which is
   * where the reader is. But deleting the `<h2>` would take a level out of the heading outline and
   * leave the section unnamed to a screen reader, so it becomes `sr-only`: the duplication is
   * visual, and so is the cure.
   */
  headingHidden?: boolean;
}

export function OrgMembersPanel({
  config,
  moduleFilter,
  onClearModuleFilter,
  appColumn,
  onLeft,
  headingHidden = false,
}: OrgPanelProps) {
  const prefix = `${testIdPrefix(config)}-members`;
  const queryClient = useQueryClient();
  const gate = useOrgAccessGate(config.orgRef);
  const list = useOrgMembers(config.orgRef);
  const [editing, setEditing] = useState<OrgConsoleMemberRow | null>(null);

  const filtered = (moduleFilter ?? '').trim();
  const inAppColumn = filtered.length === 0 ? (appColumn ?? '').trim() : '';
  const members = useMemo(() => {
    const all = list.data ?? [];
    if (filtered.length === 0) return all;
    return all.filter((member) =>
      member.modules.some((row) => row.module === filtered && row.has_access === true),
    );
  }, [list.data, filtered]);
  const canManage = gate.isGlobalAdmin;
  const absent =
    (list.isError && isOrgConsoleUnavailable(list.error)) ||
    (gate.isUnavailable && !list.isSuccess);

  /**
   * Fold the row the write answered with back into the list.
   *
   * ⚠ THE SERVER'S ROW IS WHAT LANDS, not the draft the dialog held. The backend applies the tier
   * change before the per-module patch and re-resolves everything after it (a demotion resets each
   * module's tier), so the answer is frequently NOT what the form believed it was submitting —
   * writing the draft in would show a state that never existed. The re-read that follows keeps it
   * honest; this keeps the row correct in the frame between them.
   */
  const applyUpdatedRow = useCallback(
    (updated: OrgConsoleMemberRow) => {
      queryClient.setQueryData<InfiniteData<OrgConsoleMemberRow[], number>>(
        orgConsoleKeys(config.orgRef).members,
        (current) =>
          current === undefined
            ? current
            : {
                ...current,
                pages: current.pages.map((page) =>
                  page.map((row) => (row.user_id === updated.user_id ? updated : row)),
                ),
              },
      );
    },
    [queryClient, config.orgRef],
  );

  /**
   * Drop a REMOVED admin from the roster this session is looking at, in the frame before the
   * invalidated re-read lands.
   *
   * The removal's twin of {@link applyUpdatedRow}, and it exists for the same reason: the hook
   * invalidates, but the refetch is a round trip, and leaving the row on screen for it makes the
   * confirmation look like it did nothing. Unlike an access write there is no server row to consume
   * — the operation answers 204 — so the only honest local edit is the removal itself.
   */
  const dropRemovedRow = useCallback(
    (removedUserId: string) => {
      queryClient.setQueryData<InfiniteData<OrgConsoleMemberRow[], number>>(
        orgConsoleKeys(config.orgRef).members,
        (current) =>
          current === undefined
            ? current
            : {
                ...current,
                pages: current.pages.map((page) =>
                  page.filter((row) => row.user_id !== removedUserId),
                ),
              },
      );
    },
    [queryClient, config.orgRef],
  );

  const columns: DataTableColumn<OrgConsoleMemberRow>[] = useMemo(() => {
    const base: DataTableColumn<OrgConsoleMemberRow>[] = [
      {
        id: 'name',
        header: 'Name',
        width: canManage ? '40%' : '55%',
        // ⚠ THE COLUMN HOLDS ITS WIDTH, AND A LONG NAME TRUNCATES INSIDE IT (Org Machinery OM-79).
        // The table lays out to its content, so a name's own `truncate` never fired: the column
        // grew to fit the whole name and squeezed the others until "Every app" wrapped. `truncate`
        // lets the cell shrink to the width above; the stacked reading keeps its full width.
        truncate: true,
        cell: (member) => {
          const name = orgMemberName(member);
          const email = orgMemberEmail(member);
          return (
            <div className="flex min-w-0 items-center gap-12">
              <Avatar name={name} size="sm" />
              <div className="min-w-0">
                {/* The External note follows the name on its own line (§OM-53), only on a marked
                    row. ⚠ THE NOTE NEVER TRUNCATES; THE NAME DOES: a long name gives way before
                    the label does, so a marked row still says so however its name is cut. */}
                <div className="flex min-w-0 items-baseline gap-8">
                  <div className="truncate font-w600 text-text-strong">{name}</div>
                  {member.external && (
                    <ExternalNote testId={`${prefix}-row-${member.user_id}-external`} />
                  )}
                </div>
                {/* A person whose account carries no name is named by their address, so it is
                    said once (OM-79), never again on the line beneath. */}
                {email.length > 0 && email !== name && (
                  <div className="truncate text-s13 text-text-tertiary">{email}</div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: 'tier',
        header: 'Access',
        cell: (member) => {
          // ⚠ FILTERED TO ONE APP, THE COLUMN CARRIES THE TIER IN THAT APP (shell contract v3,
          // mockup requirement 3) — never the org-level access, which would read an app's Owner as
          // "Member" and a Global Admin as a tier they hold by a different fact.
          //
          // ⚠ UNFILTERED, IT SAYS "GLOBAL ADMIN" OR NOTHING (OM-96, D-CPO-77 item 7). The org-level
          // word "Member" beside an app's Member or Owner read as a contradiction ("Access: Member,
          // Donations: Owner"), so it is said only for a Global Admin; anyone else's level is per
          // app, in the app's column where the page has one and in Manage access.
          const inModule =
            filtered.length > 0 ? member.modules.find((row) => row.module === filtered) : undefined;
          const perApp = inModule !== undefined;
          const orgWord = orgLevelWord(
            member.is_global_admin ? ROLE_OWNER : (member.base_role ?? ROLE_MEMBER),
          );
          return (
            // ⚠ THE INDENT GIVES THE STACKED BLOCK ONE LEFT EDGE. Stacked, the first line starts at
            // the name (past the 32px avatar and its 12px gap) and the meta line was returning to
            // the card's padding edge under the avatar — two internal edges, so one record read as
            // two stanzas. 44 is that avatar lane, and it is a real key on the scale.
            <span
              className="max-sm:ml-44 max-sm:inline-block"
              {...testIdProp(`${prefix}-row-${member.user_id}-tier`)}
            >
              {perApp ? (
                <Badge
                  tone={moduleTierLabel(inModule.granted_role) === ROLE_OWNER ? 'brand' : 'neutral'}
                >
                  {moduleTierLabel(inModule.granted_role)}
                </Badge>
              ) : orgWord !== null ? (
                <Badge tone="brand">{orgWord}</Badge>
              ) : null}
            </span>
          );
        },
      },
    ];
    // In an app's frame (OM-96): each person's level in that app, beside the org-level Access.
    if (inAppColumn.length > 0) {
      base.push({
        id: 'app',
        header: config.vocab.moduleLabel(inAppColumn),
        cell: (member) => {
          const row = member.modules.find((candidate) => candidate.module === inAppColumn);
          return (
            <span
              className="max-sm:ml-44 max-sm:inline-block"
              {...testIdProp(`${prefix}-row-${member.user_id}-app`)}
            >
              {row?.has_access === true ? (
                <Badge
                  tone={moduleTierLabel(row.granted_role) === ROLE_OWNER ? 'brand' : 'neutral'}
                >
                  {moduleTierLabel(row.granted_role)}
                </Badge>
              ) : (
                <span className="text-text-tertiary">{TEAM_COPY.noAccess}</span>
              )}
            </span>
          );
        },
      });
    }
    // The Apps column ("N apps") is the ORG-LEVEL summary; narrowed to one app it would restate a
    // fact the filter sentence already carries, so it is omitted in that view. It counts only the
    // apps people can open here (OM-95): a grant for an app this console does not serve is kept,
    // and not counted.
    if (filtered.length === 0) {
      base.push({
        id: 'apps',
        header: 'Apps',
        cell: (member) => (
          <span
            className="text-text-secondary"
            {...testIdProp(`${prefix}-row-${member.user_id}-apps`)}
          >
            {moduleAccessSummary({
              ...member,
              modules: member.modules.filter((row) => isServedModule(config, row.module)),
            })}
          </span>
        ),
      });
    }

    if (canManage) {
      base.push({
        id: 'actions',
        header: '',
        align: 'right' as const,
        width: '160px',
        // ⚠ OMITTED FROM THE STACKED READING (OM-13), and the row's own action replaces it. A button
        // inside a block whose action is elsewhere would be two targets in one place.
        stacked: 'omit' as const,
        cell: (member) => (
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => setEditing(member)}
            testId={`${prefix}-row-${member.user_id}-manage`}
          >
            Manage access
          </Button>
        ),
      });
    }

    return base;
  }, [canManage, prefix, filtered, inAppColumn, config]);

  if (absent) {
    return (
      <OrgConsoleUnavailableNotice
        testId={`${prefix}-unavailable`}
        title={config.vocab.membersTitle}
        description={`This environment doesn’t have the console turned on. Nothing is wrong with your account — there’s just nothing here to open yet.`}
      />
    );
  }

  return (
    <section className="flex flex-col gap-12" {...testIdProp(prefix)}>
      <div>
        <h2
          className={
            headingHidden
              ? 'sr-only'
              : 'font-sans text-s16 font-w700 tracking-givmo text-text-strong'
          }
        >
          {config.vocab.membersTitle}
        </h2>
        <p className="mt-4 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
          {config.vocab.membersDescription}
        </p>
        {filtered.length > 0 && (
          <p
            className="mt-8 flex flex-wrap items-center gap-8 font-sans text-s13 font-w400 tracking-givmo text-text-secondary"
            {...testIdProp(`${prefix}-filter`)}
          >
            <span>Showing only people who can open {config.vocab.moduleLabel(filtered)}.</span>
            {onClearModuleFilter !== undefined && (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={onClearModuleFilter}
                testId={`${prefix}-filter-clear`}
              >
                Show everyone
              </Button>
            )}
          </p>
        )}
      </div>

      <DataTable
        testId={`${prefix}-list`}
        columns={columns}
        rows={members}
        rowKey={(member) => member.user_id}
        rowTestId={() => `${prefix}-row`}
        // ⚠ THE ROSTER IS UNREADABLE AS A TABLE ON A PHONE (OM-13). At 390px it showed only NAME,
        // with the tier, the app count and "Manage access" off to the right behind a scroll nothing
        // announces — so a reader could not tell what a row said, and could not reach its action at
        // all. Below `sm` each row is a stacked block and the row itself carries the action; from
        // 640px up this is byte-identical to the table it has always been.
        stackAt="sm"
        loadingLabel="Loading your team…"
        {...(canManage
          ? {
              onStackedRowAction: (member: OrgConsoleMemberRow) => setEditing(member),
              stackedRowActionLabel: (member: OrgConsoleMemberRow) =>
                `Manage access for ${orgMemberName(member)}`,
              // ⚠ ITS OWN ID, never the button's. The two are different elements — the desktop
              // column's "Manage access" and the stacked row's chevron — and only one renders at a
              // given width, but sharing an id makes `getByTestId` ambiguous in any test or spec that
              // renders both readings, which is exactly what happened the first time this was wired.
              stackedRowActionTestId: (member: OrgConsoleMemberRow) =>
                `${prefix}-row-${member.user_id}-open`,
            }
          : {})}
        loading={list.isPending}
        error={list.isError ? 'We couldn’t load your team.' : undefined}
        onRetry={() => void list.refetch()}
        emptyState={
          <EmptyState
            testId={`${prefix}-empty-state`}
            icon={<Icon icon={Users} size={20} aria-hidden />}
            title={filtered.length > 0 ? 'Nobody holds this app' : 'No members yet'}
            description={
              filtered.length > 0
                ? // The narrowing has to explain ITSELF, or an empty table reads as an empty roster.
                  `Nobody loaded here can open ${config.vocab.moduleLabel(filtered)}.`
                : `Everyone who can act for this ${config.vocab.orgNoun} will appear here.`
            }
          />
        }
        // ⚠ NO FOOTER UNTIL A READ HAS ACTUALLY RESOLVED, and this is honesty rather than polish.
        // The count is `members.length`, which is 0 both while the first page is in flight and after
        // the read FAILED — so the panel printed "0 members" under five skeleton rows, and again
        // directly beneath "We couldn't load your team." Neither is a number this surface learned;
        // both are a claim it cannot support, which is the one thing a record of who can act for an
        // organization must never make. Caught by rendering the four states rather than reasoning
        // about them (OM-13 fold 4). `isFetchingNextPage` is deliberately NOT in the condition: a
        // pager load keeps the count it already has, which is true.
        footer={
          list.isPending || list.isError ? undefined : (
            <>
              {/* Rows LOADED — never "of N". See the header. */}
              <span
                className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums"
                {...testIdProp(`${prefix}-count`)}
              >
                {members.length} {members.length === 1 ? 'member' : 'members'}
              </span>
              {list.hasNextPage ? (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                  testId={`${prefix}-load-more`}
                >
                  {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              ) : null}
            </>
          )
        }
      />

      <OrgAccessEditorDialog
        config={config}
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        member={editing}
        selfUserId={config.selfUserId ?? null}
        onSaved={applyUpdatedRow}
        // The SAME gate the "Manage access" control itself rides — permissive, so a flaky `/me`
        // read never strands a real Global Admin, with the server as the authoritative backstop.
        // Passing it rather than letting the dialog assume it is what keeps the removal control
        // out of a future mount that has no business showing it.
        canRemove={canManage}
        onRemoved={dropRemovedRow}
        onLeft={onLeft}
      />
    </section>
  );
}
